import React, { useEffect, useState } from 'react';
import '../styles/Home.css';
import '../styles/Review.css';
import { FaMapPin } from 'react-icons/fa';
import { FaGlobe, FaInstagram } from 'react-icons/fa6';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ContactWebsite from '../components/ContactWebsite';
import ImageLightbox from '../components/ImageLightbox';
import axiosPost from '../helpers/axios-helper';
import { slugify } from '../constants/venueTypes';
import BackButton from '../components/BackButton';

/**
 * An LGBTQ+ organisation's own page, laid out like a hotel page: title,
 * photo gallery, the story on the left and contact details on the right.
 * Route: /organisation/:slug where the slug starts with the id ("12-name").
 */
export default function OrganisationDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const id = parseInt(String(slug || ''), 10) || 0;

  const [org, setOrg] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    if (!id) { setNotFound(true); return undefined; }
    const controller = new AbortController();
    axiosPost('organisations/get', { 'Content-Type': 'application/json' }, { id }, (resp) => {
      const o = resp?.data?.organisation;
      if (!resp?.success || !o) { setNotFound(true); return; }
      setOrg(o);
      // Keep the address bar in step with the current name
      const canonical = `${o.id}-${slugify(o.name)}`;
      if (slug !== canonical) navigate(`/organisation/${canonical}`, { replace: true });
    }, controller);
    return () => controller.abort();
  }, [id, slug, navigate]);

  if (notFound) {
    return (
      <div style={{ paddingTop: '120px' }} className='container'>
        <h1 className='review-title'>Organisation not found</h1>
        <p><Link to='/'>Back to the homepage</Link></p>
      </div>
    );
  }
  if (!org) return <div style={{ paddingTop: '120px' }} className='container'>Loading…</div>;

  // The gallery uses the photos; with none, the logo stands in
  const images = org.images?.length ? org.images : (org.logo ? [org.logo] : []);
  const description = (org.description || '').trim() || (org.summary ? `<p>${escapeHtml(org.summary)}</p>` : '');

  return (
    <div style={{ paddingTop: '90px' }}>
      <div className='container'>
        <BackButton />
        <div className='review-header'>
          <div className='review-header-info'>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 className='review-title'>{org.name}</h1>
              {org.focus && (
                <div style={{ background: '#f0f0f0', color: '#2c2c2c', padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>{org.focus}</div>
              )}
            </div>
            {(org.city || org.country_name) && (
              <div style={{ display: 'flex', gap: 15, flexWrap: 'wrap', marginTop: 5 }}>
                <div className='hotel-location'>
                  <FaMapPin size={12} color={'#2c2c2c'} />
                  <div className='hotel-address'>{[org.city, org.country_name].filter(Boolean).join(', ').toUpperCase()}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {images.length > 0 && (
        <div className={`review-gallery-grid ${images.length <= 1 ? 'single-image' : ''} ${images.length === 2 ? 'two-images' : ''}`}>
          <div className='review-gallery-main-image' style={{ backgroundImage: `url('${images[0]}')` }} onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}>
            <div className='review-gallery-count'><span style={{ marginRight: 6 }}>📷</span> {images.length}</div>
          </div>
          {images.length > 1 && (
            <div className='review-gallery-side'>
              {images.slice(1, 4).map((img, idx) => (
                <div key={idx} className='review-gallery-side-image' style={{ backgroundImage: `url('${img}')` }} onClick={() => { setLightboxIndex(idx + 1); setLightboxOpen(true); }}>
                  {idx === 2 && images.length > 4 && (<div className='review-gallery-more-overlay'>+{images.length - 4} more</div>)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {lightboxOpen && images.length > 0 && (
        <ImageLightbox images={images} initialIndex={lightboxIndex} onClose={() => setLightboxOpen(false)} />
      )}

      <div className='container'>
        <div className='review-content-splitter'>
          <div className='review-content'>
            {description && <div className='review-amenities-title'>About</div>}
            {description && (
              <div className='rich-text' style={{ letterSpacing: '1px', fontSize: 14, color: '#2b2b2b', marginBottom: 50, lineHeight: '1.5' }}
                dangerouslySetInnerHTML={{ __html: description.replace(/<bold>/g, '<strong>').replace(/<\/bold>/g, '</strong>').replace(/color:\s*[^;"}]+;?/gi, '') }} />
            )}
          </div>

          <div className='review-side-content'>
            {org.logo && org.images?.length > 0 && (
              <img src={org.logo} alt={`${org.name} logo`} style={{ maxWidth: 160, maxHeight: 100, objectFit: 'contain', marginBottom: 20 }} />
            )}
            {(org.address || org.website || org.instagram) && (
              <div className='review-contact-title'>CONTACT INFORMATION</div>
            )}
            {org.address && (<div className='review-contact-item'><FaMapPin size={16} color='#2c2c2c' /><div className='review-contact-text'>{org.address}</div></div>)}
            {org.website && (<div className='review-contact-item'><FaGlobe size={16} color='#2c2c2c' /><ContactWebsite url={org.website} /></div>)}
            {org.instagram && (
              <div className='review-contact-item'>
                <FaInstagram size={16} color='#2c2c2c' />
                <a className='review-contact-text' href={org.instagram} target='_blank' rel='noreferrer'>Instagram</a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function escapeHtml(text) {
  return String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
